import { localizeSpeakerDisplay } from "./speakerLabel";
import { getMaxCharsPerCaptionBlock } from "./captionSizing";

// UI-only: format "H:MM:SS" (from StreamContext) as "M:SS" for display
export function toMSS(hms) {
  const parts = String(hms).trim().split(":").map(Number);
  if (parts.length >= 3) {
    const [h, m, s] = parts;
    const totalSecs = (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }
  return hms;
}

export function formatTimestampSpeakerLine(html, langCode = "en") {
  const re =
    /<div style="display:flex; justify-content: space-between;">\s*<strong>([^<]*)<\/strong>\s*<span>([^<]*)<\/span>\s*<\/div>/g;
  let out = html.replace(re, (_, speaker, timeStr) => {
    const mss = toMSS(timeStr);
    const localized = localizeSpeakerDisplay(speaker, langCode);
    return `<div class="caption-time-speaker mb-0.5">${mss} | ${localized}</div>`;
  });
  out = out.replace(
    /(<div class="caption-time-speaker[^"]*">[\s\S]*?<\/div>)\s*<br\s*\/?>/gi,
    "$1 ",
  );
  return out;
}

function stripHtml(text) {
  return String(text)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function hardSplitByLength(text, maxChars) {
  const chunks = [];
  let rest = text;
  while (rest.length > maxChars) {
    let end = maxChars;
    const space = rest.lastIndexOf(" ", end);
    if (space > maxChars * 0.5) end = space;
    chunks.push(rest.slice(0, end).trim());
    rest = rest.slice(end).trim();
  }
  if (rest) chunks.push(rest);
  return chunks;
}

function mergeShortTrailingChunks(chunks, maxChars) {
  const result = [...chunks];
  while (result.length > 1) {
    const last = result[result.length - 1];
    const prev = result[result.length - 2];
    const combined = `${prev} ${last}`;
    if (last.length < 42 && combined.length <= maxChars) {
      result.splice(-2, 2, combined);
    } else {
      break;
    }
  }
  return result;
}

function chunkBodyText(body, maxChars) {
  const text = stripHtml(body);
  if (!text) return [];

  const sentences = text.split(/(?<=[.!?…])\s+/).filter(Boolean);
  const merged = [];
  let current = "";

  for (const sentence of sentences) {
    const candidate = current ? `${current} ${sentence}` : sentence;
    if (current && candidate.length > maxChars) {
      merged.push(current);
      current = sentence;
    } else {
      current = candidate;
    }
  }
  if (current) merged.push(current);

  const split = merged.flatMap((part) =>
    part.length > maxChars ? hardSplitByLength(part, maxChars) : [part],
  );

  return mergeShortTrailingChunks(split, maxChars);
}

function parseCaptionSegments(html) {
  const formatted = html;
  const metaRe = /<div class="caption-time-speaker[^"]*">([^<]*)<\/div>\s*/gi;
  const matches = [...formatted.matchAll(metaRe)];

  if (matches.length === 0) {
    const text = stripHtml(formatted);
    return text ? [{ meta: null, body: text }] : [];
  }

  const segments = [];
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const meta = match[1].trim();
    const bodyStart = match.index + match[0].length;
    const bodyEnd =
      i + 1 < matches.length ? matches[i + 1].index : formatted.length;
    const body = formatted.slice(bodyStart, bodyEnd);
    if (stripHtml(body)) segments.push({ meta, body });
  }
  return segments;
}

/**
 * Turn live caption payloads into display blocks: metadata + 2–3 lines of body each.
 */
export function buildCaptionDisplayBlocks(
  captions,
  langCode = "en",
  fontSizeMaxPx,
  viewportWidth,
) {
  if (!captions?.length) return [];

  const maxChars = getMaxCharsPerCaptionBlock(fontSizeMaxPx, viewportWidth);
  const combinedText = captions.map((line) => line.transcript).join(" ");
  const formatted = formatTimestampSpeakerLine(combinedText, langCode);
  const segments = parseCaptionSegments(formatted);

  const blocks = [];
  for (const segment of segments) {
    const chunks = chunkBodyText(segment.body, maxChars);
    chunks.forEach((text, index) => {
      blocks.push({
        meta: index === 0 ? segment.meta : null,
        text,
      });
    });
  }
  return blocks;
}
